import { useCallback, useEffect, useState } from "react";
import { workerService } from "@/services/worker.service";
import { requestFcmToken } from "@/lib/fcm";

interface UsePushSubscriptionReturn {
  subscribed: boolean;
  busy: boolean;
  status: string;
  subscribe: () => Promise<void>;
}

export function usePushSubscription(uuid: string): UsePushSubscriptionReturn {
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (!uuid) return;
    let cancelled = false;
    (async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
      if (Notification.permission !== "granted") return;
      const reg = await navigator.serviceWorker.ready;
      try {
        const token = await requestFcmToken(reg);
        if (token && !cancelled) {
          await workerService.registerWorkerFcmToken(uuid, token);
          setSubscribed(true);
        }
      } catch {
        return;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uuid]);

  const subscribe = useCallback(async () => {
    setBusy(true);
    setStatus("");
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        throw new Error("이 브라우저는 알림을 지원하지 않습니다.");
      }
      if (Notification.permission !== "granted") {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") {
          setStatus("알림 권한이 거부되었습니다. 브라우저 설정에서 알림을 허용해 주세요.");
          return;
        }
      }
      const reg = await navigator.serviceWorker.ready;
      const token = await requestFcmToken(reg);
      if (!token) throw new Error("FCM 토큰을 발급하지 못했습니다.");
      await workerService.registerWorkerFcmToken(uuid, token);
      setSubscribed(true);
      setStatus("알림이 설정되었습니다.");
    } catch (e) {
      if (e instanceof Error && e.name === "NotAllowedError") {
        setStatus("알림 권한이 거부되었습니다. 브라우저 설정에서 알림을 허용해 주세요.");
      } else {
        setStatus(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setBusy(false);
    }
  }, [uuid]);

  return { subscribed, busy, status, subscribe };
}
